import { useEffect, useState, useCallback, useRef } from "react";
import { useHistory, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { CheckCircleFill, Clock, XCircleFill, PersonBadgeFill, FileEarmarkArrowDownFill, XLg } from "react-bootstrap-icons";
import { usersActions } from "../../data/user";
import notify from "../../common/ui/notify";
import PageHero from "../../common/ui/PageHero";
import { LoadingBlock } from "../../common/ui/StateBlock";
import { formatPrice, formatDate, display } from "../../common/format";
import { t } from "./auctionI18n";
import "./Auction.css";
import "../../common/ui/ui.css";

// Phone verification via SMS (smsreklama.ge). Flip to `false` to hide the SMS
// step and stop requiring it.
const PHONE_VERIFICATION_ENABLED = true;

const StepBadge = ({ done, pending }) => {
    if (done) return <span className="auction-step-badge is-done"><CheckCircleFill /></span>;
    if (pending) return <span className="auction-step-badge is-pending"><Clock /></span>;
    return <span className="auction-step-badge is-todo"><XCircleFill /></span>;
};

const AuctionAccount = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [maxBid, setMaxBid] = useState("");
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get("/api/auction/me");
            setMe(res.data);
        } catch (err) {
            if (err && err.response && err.response.status === 401) {
                history.replace("/auction/login");
                return;
            }
            notify.apiError(err);
        } finally {
            setLoading(false);
        }
    }, [history]);

    useEffect(() => { load(); }, [load]);

    const verifyPhone = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            const res = await axios.post("/api/auction/verify-phone", { code: code.trim() });
            notify.success(res.data.message);
            setCode("");
            await load();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const resend = async () => {
        setBusy(true);
        try {
            const res = await axios.post("/api/auction/resend-code", {});
            notify.success(res.data.message);
            if (res.data.phone_verification && res.data.phone_verification.dev_code) {
                notify.info({ en: `Dev code: ${res.data.phone_verification.dev_code}`, geo: `სატესტო კოდი: ${res.data.phone_verification.dev_code}`, ru: `Тестовый код: ${res.data.phone_verification.dev_code}`, am: `Փորձնական կոդ: ${res.data.phone_verification.dev_code}` });
            }
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const uploadKyc = async (e) => {
        e.preventDefault();
        const files = fileRef.current && fileRef.current.files;
        if (!files || !files.length) { notify.warning({ en: "Choose a file first.", geo: "ჯერ აირჩიეთ ფაილი.", ru: "Сначала выберите файл.", am: "Նախ ընտրեք ֆայլ։" }); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("doc_type", "id_card");
            Array.from(files).forEach((f) => fd.append("documents", f));
            const res = await axios.post("/api/auction/kyc", fd, { headers: { "Content-Type": "multipart/form-data" } });
            notify.success(res.data.message);
            if (fileRef.current) fileRef.current.value = "";
            await load();
        } catch (err) { notify.apiError(err); }
        finally { setBusy(false); }
    };

    const logout = async () => {
        try { await axios.post("/api/auction/logout", {}); } catch (e) { /* ignore */ }
        dispatch(usersActions.changeIsLoggedIn(false));
        history.push("/auction/login");
    };

    if (loading) return <div className="container auction-narrow"><LoadingBlock label={t("იტვირთება...", "Loading...", "Загрузка...", "Բեռնվում է...")} /></div>;
    if (!me) return null;

    const { user, eligibility, latest_kyc } = me;
    const phoneDone = !!user.phone_verified;
    const kycDone = user.kyc_status === "approved";
    const kycPending = user.kyc_status === "pending";
    const depositOk = eligibility && Number(eligibility.deposit.available) > 0;

    return (
        <>
            <PageHero eyebrow="Carexpress" title={t("ჩემი აუქციონის ანგარიში", "My auction account", "Мой аукционный аккаунт", "Իմ աճուրդի հաշիվը")}
                subtitle={`${user.name || ""} ${user.surname || ""} · ${user.email || ""}`} />

            <div className="container auction-narrow auction-account">
                <div className="auction-account-links">
                    <Link to="/auctions" className="cx-btn cx-btn-ghost">{t("აუქციონები", "Auctions", "Аукционы", "Աճուրդներ")}</Link>
                    <Link to="/auction/my" className="cx-btn cx-btn-ghost">{t("ჩემი აუქციონები", "My auctions", "Мои аукционы", "Իմ աճուրդները")}</Link>
                </div>

                {/* Profile info */}
                <div className="cx-card auction-step">
                    <div className="auction-step-head"><span className="auction-profile-icon"><PersonBadgeFill /></span><h3>{t("პროფილის ინფორმაცია", "Profile information", "Информация профиля", "Պրոֆիլի տվյալներ")}</h3></div>
                    <dl className="auction-specs auction-profile-grid">
                        <div className="auction-spec-row"><dt>{t("სახელი, გვარი", "Name, surname", "Имя, фамилия", "Անուն, ազգանուն")}</dt><dd>{display([user.name, user.surname].filter(Boolean).join(" "))}</dd></div>
                        <div className="auction-spec-row"><dt>{t("პირადი ნომერი", "Personal ID", "Личный номер", "Անձնական համար")}</dt><dd>{display(user.personal_id)}</dd></div>
                        <div className="auction-spec-row"><dt>{t("დაბადების თარიღი", "Date of birth", "Дата рождения", "Ծննդյան ամսաթիվ")}</dt><dd>{user.birth_date ? formatDate(user.birth_date) : "—"}</dd></div>
                        <div className="auction-spec-row"><dt>{t("ელფოსტა", "Email", "Эл. почта", "Էլ. փոստ")}</dt><dd>{display(user.email)}</dd></div>
                        <div className="auction-spec-row"><dt>{t("ტელეფონი", "Phone", "Телефон", "Հեռախոս")}</dt><dd>{display(user.phone)}</dd></div>
                        <div className="auction-spec-row"><dt>{t("ქალაქი", "City", "Город", "Քաղաք")}</dt><dd>{display(user.city)}</dd></div>
                        <div className="auction-spec-row"><dt>{t("ანგარიშის ტიპი", "Account type", "Тип аккаунта", "Հաշվի տեսակ")}</dt><dd>{user.account_type === "company" ? t("იურიდიული პირი", "Legal entity", "Юр. лицо", "Իրավաբանական անձ") : t("ფიზიკური პირი", "Individual", "Физ. лицо", "Ֆիզիկական անձ")}</dd></div>
                        {user.account_type === "company" && (
                            <div className="auction-spec-row"><dt>{t("კომპანია", "Company", "Компания", "Ընկերություն")}</dt><dd>{display(user.company_name)}{user.company_id ? ` (${user.company_id})` : ""}</dd></div>
                        )}
                        <div className="auction-spec-row"><dt>{t("მარკეტინგული შეტყობინებები", "Marketing messages", "Маркетинговые уведомления", "Մարքեթինգային ծանուցումներ")}</dt><dd>{user.marketing_consent ? t("ჩართული", "On", "Включены", "Միացված") : t("გამორთული", "Off", "Выключены", "Անջատված")}</dd></div>
                    </dl>
                </div>

                {eligibility && eligibility.eligible && (
                    <div className="cx-card auction-eligible">
                        <CheckCircleFill />
                        <div>
                            <strong>{t("თქვენ მზად ხართ ბიდის დასადებად!", "You're ready to bid!", "Вы готовы делать ставки!", "Դուք պատրաստ եք հայտ դնելու!")}</strong>
                            <Link to="/auctions" className="cx-btn cx-btn-primary">{t("აუქციონებზე გადასვლა", "Go to auctions", "Перейти к аукционам", "Անցնել աճուրդներ")}</Link>
                        </div>
                    </div>
                )}

                {/* Step 1 — phone (temporarily disabled: hidden and not required to bid) */}
                {PHONE_VERIFICATION_ENABLED && (
                    <div className="cx-card auction-step">
                        <div className="auction-step-head"><StepBadge done={phoneDone} /><h3>1. {t("ტელეფონის დადასტურება", "Phone verification", "Подтверждение телефона", "Հեռախոսի հաստատում")}</h3></div>
                        {phoneDone ? (
                            <p className="auction-muted">{t(`ნომერი ${user.phone} დადასტურებულია.`, `Number ${user.phone} is verified.`, `Номер ${user.phone} подтверждён.`, `${user.phone} համարը հաստատված է։`)}</p>
                        ) : (
                            <form onSubmit={verifyPhone} className="auction-inline-form">
                                <p className="auction-muted">{t(`კოდი გამოგზავნილია ნომერზე ${user.phone}.`, `A code was sent to ${user.phone}.`, `Код отправлен на ${user.phone}.`, `Կոդն ուղարկվել է ${user.phone} համարին։`)}</p>
                                <div className="auction-inline-row">
                                    <input className="form-control" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("SMS კოდი", "SMS code", "SMS-код", "SMS կոդ")} inputMode="numeric" />
                                    <button className="cx-btn cx-btn-primary" disabled={busy}>{t("დადასტურება", "Verify", "Подтвердить", "Հաստատել")}</button>
                                    <button type="button" className="cx-btn cx-btn-ghost" onClick={resend} disabled={busy}>{t("ხელახლა გაგზავნა", "Resend", "Отправить снова", "Ուղարկել կրկին")}</button>
                                </div>
                            </form>
                        )}
                    </div>
                )}

                {/* KYC */}
                <div className="cx-card auction-step">
                    <div className="auction-step-head"><StepBadge done={kycDone} pending={kycPending} /><h3>{PHONE_VERIFICATION_ENABLED ? 2 : 1}. {t("პირადობის ვერიფიკაცია (KYC)", "Identity verification (KYC)", "Верификация личности (KYC)", "Ինքնության հաստատում (KYC)")}</h3></div>
                    {kycDone ? (
                        <p className="auction-muted">{t("პირადობა დადასტურებულია.", "Identity verified.", "Личность подтверждена.", "Ինքնությունը հաստատված է։")}</p>
                    ) : kycPending ? (
                        <p className="auction-muted">{t("დოკუმენტები განხილვის პროცესშია.", "Your documents are under review.", "Документы на рассмотрении.", "Փաստաթղթերը քննարկման մեջ են։")}</p>
                    ) : (
                        <form onSubmit={uploadKyc} className="auction-inline-form">
                            {user.kyc_status === "rejected" && latest_kyc && (
                                <p className="auction-field-error">{t("წინა მცდელობა უარყოფილია", "Previous attempt rejected", "Предыдущая попытка отклонена", "Նախորդ փորձը մերժվել է")}{latest_kyc.review_note ? `: ${latest_kyc.review_note}` : ""}. {t("ატვირთეთ თავიდან.", "Please upload again.", "Загрузите снова.", "Վերբեռնեք կրկին։")}</p>
                            )}
                            <p className="auction-muted">{t("ატვირთეთ პირადობის მოწმობა ან პასპორტი (JPG/PNG/PDF).", "Upload your ID card or passport (JPG/PNG/PDF).", "Загрузите удостоверение или паспорт (JPG/PNG/PDF).", "Վերբեռնեք անձնագիր կամ ID քարտ (JPG/PNG/PDF)։")}</p>
                            <div className="auction-inline-row">
                                <input className="form-control" type="file" ref={fileRef} accept="image/*,application/pdf" multiple />
                                <button className="cx-btn cx-btn-primary" disabled={busy}>{t("ატვირთვა", "Upload", "Загрузить", "Վերբեռնել")}</button>
                            </div>
                        </form>
                    )}
                </div>

                {/* Deposit */}
                <div className="cx-card auction-step">
                    <div className="auction-step-head"><StepBadge done={depositOk} /><h3>{PHONE_VERIFICATION_ENABLED ? 3 : 2}. {t("დეპოზიტი", "Deposit", "Депозит", "Ավանդ")}</h3></div>
                    {eligibility && (
                        <div className="auction-deposit">
                            <div><span className="auction-muted">{t("ხელმისაწვდომი", "Available", "Доступно", "Հասանելի")}</span><strong>{formatPrice(eligibility.deposit.available, "GEL")}</strong></div>
                            <div><span className="auction-muted">{t("დადასტურებული", "Confirmed", "Подтверждено", "Հաստատված")}</span><strong>{formatPrice(eligibility.deposit.confirmed, "GEL")}</strong></div>
                            <div><span className="auction-muted">{t("დაკავებული", "Held", "Удержано", "Պահված")}</span><strong>{formatPrice(eligibility.deposit.held, "GEL")}</strong></div>
                        </div>
                    )}
                    {!depositOk && (
                        <p className="auction-muted auction-deposit-hint">
                            {t(
                                "დეპოზიტის შესატანად გადარიცხეთ თანხა საბანკო ანგარიშზე და დაგვიკავშირდით — ოპერატორი დაადასტურებს. ბიდის დასადებად ბალანსზე თავისუფალი უნდა იყოს ბიდის 10%.",
                                "To add a deposit, transfer the amount to the bank account and contact us — an operator will confirm it. To bid you need 10% of the bid free on your balance.",
                                "Чтобы внести депозит, переведите сумму на банковский счёт и свяжитесь с нами — оператор подтвердит. Для ставки на балансе должно быть свободно 10% от ставки.",
                                "Ավանդ ավելացնելու համար փոխանցեք գումարը բանկային հաշվին և կապվեք մեզ հետ — օպերատորը կհաստատի։ Հայտ դնելու համար հաշվեկշռին պետք է ազատ լինի հայտի 10%-ը։"
                            )}
                        </p>
                    )}
                    <div className="auction-deposit-actions">
                        <button type="button" className="cx-btn cx-btn-primary" onClick={() => { setMaxBid(""); setInvoiceOpen(true); }}>
                            <FileEarmarkArrowDownFill /> {t("ინვოისის ჩამოტვირთვა", "Download invoice", "Скачать счёт", "Ներբեռնել հաշիվը")}
                        </button>
                        <span className="auction-muted">{t("დეპოზიტის შესავსები ინვოისი — ჩასარიცხი თანხა იქნება ბიდის მაქს. თანხის 10%.", "Deposit top-up invoice — the amount to transfer is 10% of your max bid.", "Счёт на пополнение депозита — сумма к оплате 10% от макс. ставки.", "Ավանդի համալրման հաշիվ — փոխանցելու գումարը առավելագույն հայտի 10%-ն է։")}</span>
                    </div>
                </div>

                <button type="button" className="cx-btn cx-btn-ghost auction-logout" onClick={logout}>{t("გასვლა", "Sign out", "Выход", "Ելք")}</button>
            </div>

            {invoiceOpen && (() => {
                const n = Number(maxBid);
                const valid = Number.isFinite(n) && n > 0;
                const deposit = valid ? Math.round(n * 0.1) : 0;
                const generate = () => { if (valid) history.push(`/auction/deposit-invoice?max=${Math.round(n)}`); };
                return (
                    <div className="auction-modal-overlay" onClick={() => setInvoiceOpen(false)}>
                        <div className="auction-modal cx-card" onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="auction-modal-close" aria-label={t("დახურვა", "Close", "Закрыть", "Փակել")} onClick={() => setInvoiceOpen(false)}><XLg /></button>
                            <h3>{t("დეპოზიტის ინვოისი", "Deposit invoice", "Счёт на депозит", "Ավանդի հաշիվ")}</h3>
                            <p className="auction-muted">{t("მიუთითეთ მაქსიმალური თანხა, რომელზეც გსურთ ბიდის დადების შესაძლებლობა. ინვოისში ჩასარიცხ თანხად აისახება ამ თანხის", "Enter the maximum amount you want to be able to bid. The invoice shows", "Укажите максимальную сумму, на которую хотите иметь возможность ставить. В счёте будет указано", "Նշեք առավելագույն գումարը, որով ցանկանում եք հայտ դնել։ Հաշվում կցուցադրվի այդ գումարի")} <strong>10%</strong>.</p>
                            <label className="auction-modal-label">{t("ბიდის დადების მაქსიმალური თანხა (₾)", "Maximum bid amount (₾)", "Максимальная сумма ставки (₾)", "Հայտի առավելագույն գումար (₾)")}</label>
                            <input className="form-control" inputMode="numeric" autoFocus placeholder={t("მაგ: 60000", "e.g. 60000", "напр. 60000", "օր․ 60000")} value={maxBid}
                                onChange={(e) => setMaxBid(e.target.value.replace(/[^\d.]/g, ""))}
                                onKeyDown={(e) => { if (e.key === "Enter") generate(); }} />
                            <div className="auction-modal-preview">
                                <span className="auction-muted">{t("ჩასარიცხი თანხა (10%)", "Amount to transfer (10%)", "Сумма к оплате (10%)", "Փոխանցելու գումար (10%)")}</span>
                                <strong>{valid ? `${deposit.toLocaleString("en-US")} ₾` : "—"}</strong>
                            </div>
                            <button type="button" className="cx-btn cx-btn-primary auction-modal-go" disabled={!valid} onClick={generate}>
                                <FileEarmarkArrowDownFill /> {t("დააგენერირე ინვოისი", "Generate invoice", "Сгенерировать счёт", "Ստեղծել հաշիվ")}
                            </button>
                        </div>
                    </div>
                );
            })()}
        </>
    );
};

export default AuctionAccount;
